import { Controller } from "@hotwired/stimulus"
import { showOpsToast, csrfToken, formatErrors } from "../lib/ops_toast"

// Email/SMS receipt send. Submits the interaction via JSON instead of a
// full-page form post so the cashier gets an explicit result — nothing on
// this screen visually changes on a successful send the way an updated
// card does elsewhere in ops, so silence here reads as "did that work?"
// with no way to tell.
//
// Doubles as the "no destination on file" prompt (see paid_receipt.html.erb):
// when the order has no email/phone, the form carries an `input` target the
// cashier fills in at send time instead of a channel already known. Its
// value is submitted under `paramKey` and folded into the confirmation
// toast so the cashier sees exactly where it went.
export default class extends Controller {
  static targets = ["input"]
  static values = {
    channel: String, // "Emailed" / "Texted" — past tense, feeds the success toast
    paramKey: String,
    // Where it will go when the order already has one on file. Without this the
    // one-tap confirmation read "Emailed receipt" with no address, so a cashier
    // could not tell whether it reached the right customer — which is the only
    // reason the confirmation exists.
    destination: String
  }

  async send(event) {
    event.preventDefault()

    const typed = this.hasInputTarget ? this.inputTarget.value.trim() : null
    if (this.hasInputTarget && typed === "") {
      showOpsToast(`Enter ${this.channelValue === "Emailed" ? "an email address" : "a phone number"}`, "error")
      return
    }

    const button = this.element.querySelector("button")
    if (button) button.disabled = true

    try {
      // Only a typed destination is submitted; a known one is already on the
      // order and re-sending it would be a no-op write.
      const destination = typed || this.destinationValue
      const formData = new FormData()
      if (typed && this.hasParamKeyValue) {
        formData.append(`interaction[${this.paramKeyValue}]`, typed)
      }

      const response = await fetch(this.element.action, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "X-CSRF-Token": csrfToken()
        },
        body: formData
      })

      if (response.ok) {
        showOpsToast(`${this.channelValue} receipt${destination ? ` to ${destination}` : ""}`, "info")
        if (this.hasInputTarget) this.inputTarget.value = ""
        return
      }

      const payload = await response.json().catch(() => ({}))
      showOpsToast(formatErrors(payload, "Send failed"), "error")
    } catch (err) {
      showOpsToast(`Network error: ${err.message}`, "error")
    } finally {
      if (button) button.disabled = false
    }
  }
}
